/*
 *         ==== IMPORTANT NOTE ====
 *
 *           Keep it in sync with
 *           ggs-element-position
 *
 *         ==== IMPORTANT NOTE ====
 */

import {
  noop, wrap, assign, includes,
  property, zipObject, isFunction, upperFirst
} from 'lodash-es'

import idlize from './idlize'
import embedResize from './embed-resize'

const FLAGS = [
  'viewportWidth',
  'viewportHeight',
  'top',
  'bottom',
  'width',
  'height',
  'topOffset',
  'bottomOffset',
  'absTopOffset',
  'absBottomOffset',
  'displayed',
  'inViewport',
  'fitViewport',
  'fillViewport',
  'topInViewport',
  'bottomInViewport',
  'topAboveViewport',
  'topBelowViewport',
  'bottomAboveViewport',
  'bottomBelowViewport'
]

const regs = []
const irecalc = idlize(recalc)
const shorthands = zipObject(FLAGS.map(upperFirst), FLAGS.map(Shorthand))

export default assign(EmbedPosition, shorthands)

function Shorthand(flag) {
  return function shorthand(callback) {
    return EmbedPosition(property(flag), callback)
  }
}

function EmbedPosition(query, callback) {
  const reg = {
    query,
    count: 0,
    input: undefined,
    result: undefined,
    callbacks: isFunction(callback) ? [callback] : []
  }

  reg.attach = wrap(reg, attach)
  reg.detach = wrap(reg, detach)
  reg.destroy = wrap(reg, release)

  regs.push(reg)

  embedResize.position.on(irecalc)
  if (embedResize.isPositionReady()) {
    irecalc()
  }

  return reg
}

function attach(reg, callback) {
  if (!includes(reg.callbacks, callback)) {
    reg.callbacks.push(callback)
    setTimeout(InitCallback(reg, callback), 33)
  }
}

function InitCallback(reg, callback) {
  const last = reg.count
  return function initCallback() {
    if (reg.count === last && reg.input && includes(reg.callbacks, callback)) {
      callback(reg.result)
    }
  }
}

function detach(reg, callback) {
  const index = reg.callbacks.indexOf(callback)
  if (index !== -1) {
    reg.callbacks.splice(index, 1)
  }
}

function release(reg) {
  const index = regs.indexOf(reg)

  reg.count = Infinity
  reg.input = undefined
  reg.result = undefined
  reg.callbacks = []

  reg.query = noop
  reg.attach = noop
  reg.detach = noop
  reg.destroy = noop

  if (index !== -1) {
    regs.splice(index, 1)
  }

  if (regs.length === 0) {
    embedResize.position.off(irecalc)
  }
}

function recalc() {
  const input = Input()
  const localRegs = regs.concat()
  for (const reg of localRegs) {
    const result = reg.query(input)
    reg.input = input
    if (reg.result === result) {
      continue
    }

    reg.count++
    reg.result = result

    const localCallbacks = reg.callbacks.concat()
    for (const callback of localCallbacks) {
      callback(result)
    }
  }
}

function Input() {
  const viewportWidth = NaN
  const viewportHeight = embedResize.getViewportHeight()

  const top = embedResize.getViewportOffset() - embedResize.getFrameHeight()
  const bottom = embedResize.getViewportOffset()

  const width = NaN
  const height = embedResize.getFrameHeight()

  const topOffset = top
  const bottomOffset = viewportHeight - bottom

  const absTopOffset = Math.abs(topOffset)
  const absBottomOffset = Math.abs(bottomOffset)

  const displayed = (width !== 0) && (height !== 0)

  const inViewport = (topOffset < viewportHeight) && (bottomOffset < viewportHeight)
  const fitViewport = (topOffset >= 0) && (bottomOffset >= 0)
  const fillViewport = (topOffset <= 0) && (bottomOffset <= 0)

  const topInViewport = (topOffset >= 0) && (topOffset < viewportHeight)
  const bottomInViewport = (bottomOffset >= 0) && (bottomOffset < viewportHeight)

  const topAboveViewport = topOffset < 0
  const topBelowViewport = topOffset >= viewportHeight

  const bottomAboveViewport = bottomOffset >= viewportHeight
  const bottomBelowViewport = bottomOffset < 0

  const calcFillViewportPart = (fraction) => {
    const limit = viewportHeight * (1 - fraction)
    return false
      || (topOffset <= 0) && (bottomOffset <= limit)
      || (topOffset <= limit) && (bottomOffset <= 0)
  }

  return {
    viewportWidth,
    viewportHeight,

    top,
    bottom,

    width,
    height,

    topOffset,
    bottomOffset,

    absTopOffset,
    absBottomOffset,

    displayed,

    inViewport,
    fitViewport,
    fillViewport,

    topInViewport,
    bottomInViewport,

    topAboveViewport,
    topBelowViewport,

    bottomAboveViewport,
    bottomBelowViewport,

    calcFillViewportPart
  }
}
